/* eslint-disable camelcase */
/* eslint-disable no-dupe-class-members */
import { sleep } from '@pegasus/utils';
import { isClient } from './lib/utils';
import { Tracker } from './tracker';
import { isGA4Event, isPageViewEvent, matchConsentUpdateEvent, matchGA4PageViewEvent, } from './utils';
const internalTracker = new Tracker();
/**
 * A `Tracker` class that provides tracking capabilities,
 * with a built-in GA4 event schemas type checking,
 * as well as the ability to extend the schema
 * with custom events through TypeScript generics.
 *
 * @example
 * ```ts
 * const tracker = new Tracker<{ custom_event: never }>;
 * ```
 *
 * GA4's events are capable of using the queue mechanism
 * to ensure that they are tracked in the correct order.
 * By default, all the GA4 events are queued until the `GA4-page_view` event is tracked.
 *
 * Additionally, it is possible to define custom matching logic for the queue mechanism
 * with the `triggerCondition` option.
 *
 * @example
 * ```ts
 * tracker.track('GA4-EEC-add_to_cart', { ... }, {
 *     triggerCondition: ({ event_name, select_content }) =>
 *         event_name === 'select_content' && select_content.element === 'cta'
 * });
 * ```
 *
 * @category API
 */
export class QueuedTracker {
    constructor(config = {}) {
        this.tracker = internalTracker;
        this.internalQueue = this.tracker.internalQueue;
        this.internalDataLayer = this.tracker.internalDataLayer;
        this.config = {
            defaultTriggerCondition: matchGA4PageViewEvent,
            defaultPageViewTriggerCondition: matchConsentUpdateEvent,
            enableQueue: true,
            defaultEventTimeout: 250,
        };
        this.config = {
            ...this.config,
            ...config,
        };
    }
    /**
     * @private
     */
    get _queue() {
        return this.internalQueue.queue;
    }
    /**
     * @private
     */
    get _internalEventsLayer() {
        return this.internalDataLayer.events;
    }
    trackWithQueue(event, payload, options = {}) {
        const { eventCallback, eventTimeout, triggerCondition, ...queueConfig } = options;
        if (eventTimeout && eventCallback) {
            if (process.env.NODE_ENV === 'development' && triggerCondition) {
                // eslint-disable-next-line no-console
                console.error('[@pegasus/tracker] The `triggerCondition` option is not compatible with `eventTimeout` and `eventCallback` options. Ignoring queueing mechanism.');
            }
            this.tracker.trackWithQueue(event, payload, options);
            this.internalQueue.consumeQueueEntriesMatchingEvent(event, payload);
            return undefined;
        }
        /**
         * When the page_view event is received before the consent update event is tracked,
         * we need to queue the event until the consent update event is tracked (unless custom matching logic is provided).
         */
        if (isPageViewEvent(event) && !triggerCondition && !this.internalQueue.isConsentTracked) {
            this.internalQueue.addToQueue(event, payload, this.config.defaultPageViewTriggerCondition, queueConfig);
            return undefined;
        }
        /**
         * When the event is not a page_view event and the page_view event has not been tracked yet,
         * we need to queue the event until the page_view event is tracked (unless custom matching logic is provided).
         */
        if (!isPageViewEvent(event) &&
            !triggerCondition &&
            !this.internalDataLayer.isPageViewTracked) {
            this.internalQueue.addToQueue(event, payload, this.config.defaultTriggerCondition, queueConfig);
            return undefined;
        }
        /**
         * When the event is received after page_view and consent update events are tracked
         * or custom matching logic is provided, we need to check whether the condition
         * can be satisfied with the current internal events layer.
         *
         * If yes - we can track the event and consume the queue entries that match the condition.
         * If no - we need to queue the event until the condition can be satisfied.
         */
        if (triggerCondition) {
            const isEventReadyToBeFired = Array.from(this.internalDataLayer.events).find(triggerCondition);
            if (isEventReadyToBeFired) {
                this.tracker.trackWithQueue(event, payload);
                this.internalQueue.consumeQueueEntriesMatchingEvent(event, payload);
                return undefined;
            }
            this.internalQueue.addToQueue(event, payload, triggerCondition, queueConfig);
            return undefined;
        }
        this.tracker.trackWithQueue(event, payload);
        this.internalQueue.consumeQueueEntriesMatchingEvent(event, payload);
        return undefined;
    }
    /**
     * Use this method whenever navigation happens and the new page is loaded.
     *
     * This will make sure that all the events that were in the queue are flushed,
     * and we will collect and order the events that are meant for the new page.
     */
    resetTrackerQueue() {
        this.internalQueue.flushQueue();
        this.internalDataLayer.clear();
        /**
         * This is a special event that is used to track the SPA navigation,
         * so that we can set up custom trigger conditions in the GTM.
         */
        this.tracker.track('GA4-virtual_navigation');
    }
    track(event, payload, options) {
        if (isClient()) {
            if (this.config.enableQueue && isGA4Event(event)) {
                this.trackWithQueue(event, payload, options);
                return undefined;
            }
            this.tracker.track(event, payload, options);
            return undefined;
        }
        if (process.env.NODE_ENV === 'development') {
            throw new Error('[@pegasus/tracker] Attempted to use track on the server side. This method should only be used on the client side.');
        }
    }
    /**
     * Track GA4 event with predefined schema. This function resolves once the event
     * is tracked by GTM, or in an event of an issue with the tracking,
     * as soon as the specified timeout (`options.eventTimeout`) is reached.
     *
     * The list of supported GA4 events can be found in the [modules section of the documentation](https://pegasus-tracker.docs-test.zalando.net/modules.html).
     *
     * Tracking GA4 promised events ignore queue mechanism.
     *
     * @example
     * const tracker = new Tracker();
     *
     * await tracker.trackAsync('GA4-begin-signup', { ... });
     *
     * window.location.assign(...);
     */
    async trackAsync(event, payload, options = {
        eventTimeout: this.config.defaultEventTimeout,
    }) {
        if (this.internalQueue.isConsentTracked && (window === null || window === void 0 ? void 0 : window.google_tag_manager)) {
            const scheduledEvent = new Promise(resolve => {
                this.track(event, payload, {
                    ...options,
                    eventCallback: () => resolve(),
                });
            });
            return Promise.race([sleep(options.eventTimeout), scheduledEvent]);
        }
        this.track(event, payload);
        return Promise.resolve();
    }
}
// Tracker instance
export const tracker = new QueuedTracker();
export default QueuedTracker;
//# sourceMappingURL=queued-tracker.js.map